import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { lobbyDayApi } from '../lib/api'
import type { AddTargetRequest, UpdateTargetRequest } from '../types/lobbyDay'

// ---------- Target coverage ----------

export function useTargetCoverage(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'targets', eventId],
    queryFn: async () => (await lobbyDayApi.getTargets(eventId!)).data,
    enabled: !!eventId,
  })
}

export function useAddTarget(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: AddTargetRequest) => (await lobbyDayApi.addTarget(eventId, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'targets', eventId] }),
  })
}

export function useUpdateTarget(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ targetId, data }: { targetId: string; data: UpdateTargetRequest }) => {
      await lobbyDayApi.updateTarget(eventId, targetId, data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'targets', eventId] }),
  })
}

export function useRemoveTarget(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (targetId: string) => { await lobbyDayApi.removeTarget(eventId, targetId) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'targets', eventId] }),
  })
}

export function useSetEventTargetAll(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (targetAll: boolean) => { await lobbyDayApi.setEventTargetAll(eventId, { targetAll }) },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'targets', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'schedule', eventId] })
    },
  })
}

export function useSetMemberTarget(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ targetId, isTarget }: { targetId: string; isTarget: boolean }) => {
      await lobbyDayApi.setMemberTarget(eventId, targetId, { isTarget })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'targets', eventId] }),
  })
}

// ---------- Re-invite ----------

export function usePriorEvents(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'prior-events', eventId],
    queryFn: async () => (await lobbyDayApi.getPriorEvents(eventId!)).data,
    enabled: !!eventId,
  })
}

export function usePriorEventAttendees(eventId: string, priorEventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['lobby-day', 'prior-attendees', eventId, priorEventId],
    queryFn: async () => (await lobbyDayApi.getPriorEventAttendees(eventId, priorEventId!, { page: 1, pageSize: 25 })).data,
    enabled: enabled && !!priorEventId,
  })
}
